import { RegisterContract } from "@/Account/application/Register"
import { RegisterRepository } from "@/Account/domain/RegisterRepository"
import { SignUpTranslation } from "@/AutoEnrollment/ui/autoEnrollmentTranslation"
import { dniOrCifSchema, nonEmptyStringSchema, useMetaResponseHandler, validateMetaResponse } from "@/Shared/ui/form"
import { useLoader } from "@/Shared/ui/loader"
import { openNavigationRoutes } from "@/Shared/ui/routers"
import { formTranslation } from "@/Shared/ui/sharedTranslation"
import { yupResolver } from "@hookform/resolvers/yup"
import { TFunction } from "i18next"
import { useForm, UseFormReturn } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { object, ObjectSchema } from "yup"

export interface IndentifyForm {
  backOfficeCompanyId: string
  legalId: string
}
interface VerifyIdentityResponse {
  success: boolean
  error?: string
}

export interface IRegisterController {
  identifyForm: UseFormReturn<IndentifyForm>
  verifyIdentity: (data: IndentifyForm) => Promise<VerifyIdentityResponse>
  resendEmail: (data: IndentifyForm) => Promise<string>
}

const identifySchema = (t: TFunction<'translation', undefined>): ObjectSchema<IndentifyForm> =>
  object({
    backOfficeCompanyId: nonEmptyStringSchema(t)
      .min(4, t(formTranslation.errors.minChars, { value: 4 }))
      .matches(/^\d{4,}$/, t(formTranslation.errors.invalidmerchantIdFormat))
      .required(),
    legalId: dniOrCifSchema(t).required(),
  })

export const useRegisterController = (
  repository: RegisterRepository
): IRegisterController => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { startLoading, stopLoading } = useLoader()
  const { handleMetaResponse } = useMetaResponseHandler()


  const identifyForm = useForm<IndentifyForm>({
    resolver: yupResolver(identifySchema(t)),
    mode: 'onBlur',
  })

const verifyIdentity = async (data: IndentifyForm): Promise<VerifyIdentityResponse> => {
    startLoading()
    const response = await RegisterContract(repository).verifyIdentity(data)
    stopLoading()
 
    const valid = validateMetaResponse(response?.meta)
 
    if (valid) {
      navigate(openNavigationRoutes.registerEmailSuccess, {
        state: {
          fromCreateAccount: true,
          email: response?.data,
          backOfficeCompanyId: data.backOfficeCompanyId,
          legalId: data.legalId
        }
      })
    } else {
      if (response?.meta.messages.length > 0) {
        const errorCode = response.meta.messages[0].code
 
        return {
          success: valid,
          error: errorCode
        }
      }
 
    }
 
    return {
      success: valid
    }
  }

  const resendEmail = async (data: IndentifyForm): Promise<string> => {
    startLoading()
    const response = await RegisterContract(repository).resendEmail(data)
    stopLoading()

    const valid = handleMetaResponse(response?.meta, undefined, {
      successMessage: t(SignUpTranslation.otp.notificationSent),
    })

    return valid ? response.data ?? '' : ''
  }

  return {
    identifyForm,
    verifyIdentity,
    resendEmail,
  }

}